import React, {useContext, createContext, useMemo, useCallback, useRef, useEffect, useState} from 'react';
import type {AstView} from 'rendition-core';

type PreviewEventListenerCallBack = (e: React.MouseEvent, id?: string) => boolean;
type PreviewEventListenerCall = (e: React.MouseEvent, id?: string) => void;

type CallBackTypes = 'onNodeRightClick';

export interface PreviewContextArgs
    extends Omit<
        PreviewState,
        | 'onNodeRightClick'
        | 'unclickableNodes'
        | 'setUnclickableNodes'
        | 'addListener'
        | 'removeListener'
        | 'setHiddenNodes'
        | 'setTranslucentNodes'
    > {
    unclickableNodes?: string[];
    setUnclickableNodes?: (unclickableNodes: string[]) => void;
}

export interface PreviewState {
    view: AstView;
    onNodeMouseEnter: (e: React.MouseEvent, id?: string) => boolean;
    onNodeMouseLeave: (e: React.MouseEvent, id?: string) => boolean;
    onNodeClick: (e: React.MouseEvent, id?: string) => boolean;
    hiddenNodes: string[];
    setHiddenNodes: (hiddenNodes: string[]) => void;

    onNodeRightClick: PreviewEventListenerCall;
    addListener: (type: CallBackTypes, callbacks: PreviewEventListenerCallBack) => PreviewEventListenerCallBack;
    removeListener: (type: CallBackTypes, callbacks: PreviewEventListenerCallBack) => void;
    translucentNodes: string[];
    setTranslucentNodes: (translucentNodes: string[]) => void;
    unclickableNodes: string[];
    setUnclickableNodes: (unclickableNodes: string[]) => void;
}

const PreviewContext = createContext<PreviewState>({
    view: {} as AstView,
    hiddenNodes: [],
    setHiddenNodes: () => {},
    onNodeClick: () => true,
    onNodeMouseEnter: () => true,
    onNodeMouseLeave: () => true,
    onNodeRightClick: () => true,
    addListener: (type, callback) => callback,
    removeListener: (type, callback) => {},
    translucentNodes: [],
    setTranslucentNodes: () => {},
    unclickableNodes: [],
    setUnclickableNodes: () => {},
});

export const usePreviewContext = () => {
    return useContext(PreviewContext);
};

const PreviewContextProvider: React.FC<React.PropsWithChildren<{value: PreviewContextArgs}>> = ({
    children,
    value: {
        view,
        onNodeClick,
        onNodeMouseEnter,
        onNodeMouseLeave,
        hiddenNodes,
        translucentNodes,
        unclickableNodes = [],
        setUnclickableNodes = () => {},
    },
}) => {
    const [hiddenNodesState, setHiddenNodes] = useState(hiddenNodes);
    const [translucentNodesState, setTranslucentNodes] = useState(translucentNodes);

    const callBackRefs = useRef<{
        onNodeRightClick: PreviewEventListenerCallBack[];
    }>({
        onNodeRightClick: [],
    });

    /**
     * Hold onto the returned function to remove it later
     */
    const addListener = useCallback((type: CallBackTypes, fn: PreviewEventListenerCallBack) => {
        if (!callBackRefs.current) {
            return fn;
        }
        const wrappedFunction = createConsumedEvent(fn);
        callBackRefs.current[type].push(wrappedFunction);
        return wrappedFunction;
    }, []);

    /**
     * Must had correct type and function that was returned from addListener.
     */
    const removeListener = useCallback((type: CallBackTypes, fn: PreviewEventListenerCallBack) => {
        if (!callBackRefs.current) {
            return;
        }
        callBackRefs.current[type] = callBackRefs.current[type].filter((listenerFn) => listenerFn !== fn);
    }, []);

    const value = useMemo(
        () => ({
            view,
            onNodeClick: createConsumedEvent(onNodeClick),
            onNodeMouseEnter: createConsumedEvent(onNodeMouseEnter),
            onNodeMouseLeave: createConsumedEvent(onNodeMouseLeave),
            onNodeRightClick: (e, id) => callBackRefs.current.onNodeRightClick.forEach((fn) => fn(e, id)),
            hiddenNodes: hiddenNodesState,
            setHiddenNodes,
            translucentNodes: translucentNodesState,
            setTranslucentNodes,
            addListener,
            removeListener,
            setUnclickableNodes,
            unclickableNodes,
        }),
        [
            view,
            onNodeClick,
            onNodeMouseEnter,
            onNodeMouseLeave,
            hiddenNodesState,
            translucentNodesState,
            addListener,
            removeListener,
            setUnclickableNodes,
            unclickableNodes,
        ]
    );

    return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
};

export default PreviewContextProvider;

const createConsumedEvent = (inputFunction: PreviewEventListenerCallBack) => {
    let last = {};

    // wrap function.
    // check if event === last.
    // does equal last stop.
    // else  Call inputFunction. If inputFunction returns true set last.
    return (e: React.MouseEvent, id?: string) => {
        if (e === last) {
            return false;
        }

        const result = inputFunction(e, id);
        if (result) {
            last = e;
        }
        return result;
    };
};

export const usePreviewListener = (type: CallBackTypes, fn: PreviewEventListenerCallBack) => {
    const context = useContext(PreviewContext);

    useEffect(() => {
        const added = context.addListener(type, fn);

        return () => {
            context.removeListener(type, added);
        };
    }, [type, fn, context]);
};
